import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useMutation} from '@apollo/client';
import {Button, Tabs, Empty, message} from 'antd';
import AbandonConfirmModal from './AbandonConfirmModal';
import {useTranslation} from 'react-i18next';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import {useCurrentDsp} from '../../../../hooks/useCurrentDsp';
import {useWarehouseContext} from '../../../../contexts/WarehouseContext';
import {useCustomerComplaintList} from '../hooks/useCustomerComplaintList';
import {useCustomerComplaintTabCounts} from '../hooks/useCustomerComplaintTabCounts';
import {
    useAbandonCustomerComplaintAppeal,
    useAcknowledgeCustomerComplaint,
} from '../hooks/useCustomerComplaintActions';
import {
    CustomerComplaint,
    CustomerComplaintHqListFilter,
    CustomerComplaintListFilter,
    CustomerComplaintTab,
    TAB_TO_QUERY_PARAMS,
    WorkbenchMode,
} from '../graphql/types/CustomerComplaintType';
import CustomerComplaintTable from './CustomerComplaintTable';
import CustomerComplaintDetailDrawer from './CustomerComplaintDetailDrawer';
import CustomerComplaintFilter, {
    CustomerComplaintFilters,
    getDefaultCustomerComplaintFilters,
} from './CustomerComplaintFilter';
import ParcelsDownloadModal from '../../../central/parcels/components/ParcelsDownloadModal';
import {useDownloadCooldown} from '../../../../hooks/useDownloadCooldown';
import {getUserInfo} from '../../../../utils/auth';
import {
    GENERATE_CUSTOMER_COMPLAINT_DOWNLOAD_REPORT,
    GenerateCustomerComplaintDownloadReportResponse,
    GenerateCustomerComplaintDownloadReportVariables,
} from '../graphql/mutations/generateCustomerComplaintDownloadReport';
import {COMPLAINT_SOURCE_FILTER_TO_BACKEND} from '../utils/complaintSource';
import {extractGraphQLMessageKey} from '../../../../utils/graphqlUtils';

const DEFAULT_PAGE_SIZE = 20;

const TOO_MANY_TRACKING_NUMBERS_KEY = 'errors.business.customerComplaint.tooManyTrackingNumbers';
/** 与后端 PageQuery.MAX_OFFSET=10000 对齐：最多翻 500 页（500 * 20） */
const MAX_PAGES = 500;

interface CustomerComplaintPanelProps {
    /** 视角；默认 DSP，wms / HQ 路由侧需显式传对应值。语义见 WorkbenchMode 定义 */
    mode?: WorkbenchMode;
}

const toBaseFilterInput = (
    filters: CustomerComplaintFilters,
    dspId: number | null,
    warehouseId: number,
): Omit<CustomerComplaintListFilter, 'statusList' | 'isFinished'> => {
    return {
        // DSP 视角用登录态 dspId；wms 视角用筛选器里选的（未选=看全部 DSP）
        dspId: dspId ?? filters.dspId ?? undefined,
        warehouseId,
        trackingNumbers: filters.trackingNumbers?.trim() || undefined,
        driverId: filters.driverId ?? undefined,
        complaintType: filters.complaintType || undefined,
        // 来源 UI 选 客服 / 系统 时，展开为后端 source 多值；后端会优先用 sourceList
        sourceList: filters.source
            ? COMPLAINT_SOURCE_FILTER_TO_BACKEND[filters.source]
            : undefined,
        createdAtFrom: filters.createdAt?.[0].startOf('day').toISOString(),
        createdAtTo: filters.createdAt?.[1].endOf('day').toISOString(),
        appealSubmittedAtFrom: filters.appealSubmittedAt?.[0].startOf('day').toISOString(),
        appealSubmittedAtTo: filters.appealSubmittedAt?.[1].endOf('day').toISOString(),
        finishedAtFrom: filters.finishedAt?.[0].startOf('day').toISOString(),
        finishedAtTo: filters.finishedAt?.[1].endOf('day').toISOString(),
    };
};

/** HQ 全美视角：filter 不含 warehouseId；dspId 不带（看全部 DSP） */
const toHqBaseFilterInput = (
    filters: CustomerComplaintFilters,
): Omit<CustomerComplaintHqListFilter, 'statusList' | 'isFinished'> => {
    return {
        // HQ 的仓库/DSP 均来自筛选器，未选则不带该条件（分别表示全美、全部 DSP）
        warehouseId: filters.warehouseId ?? undefined,
        dspId: filters.dspId ?? undefined,
        trackingNumbers: filters.trackingNumbers?.trim() || undefined,
        driverId: filters.driverId ?? undefined,
        complaintType: filters.complaintType || undefined,
        sourceList: filters.source
            ? COMPLAINT_SOURCE_FILTER_TO_BACKEND[filters.source]
            : undefined,
        createdAtFrom: filters.createdAt?.[0].startOf('day').toISOString(),
        createdAtTo: filters.createdAt?.[1].endOf('day').toISOString(),
        appealSubmittedAtFrom: filters.appealSubmittedAt?.[0].startOf('day').toISOString(),
        appealSubmittedAtTo: filters.appealSubmittedAt?.[1].endOf('day').toISOString(),
        finishedAtFrom: filters.finishedAt?.[0].startOf('day').toISOString(),
        finishedAtTo: filters.finishedAt?.[1].endOf('day').toISOString(),
    };
};

const CustomerComplaintPanel: React.FC<CustomerComplaintPanelProps> = ({mode = 'DSP'}) => {
    const {t, i18n} = useTranslation();
    const {dspId} = useCurrentDsp();
    // wms 与 HQ 均为只读视角，仅 DSP 可操作
    const isViewOnly = mode !== 'DSP';
    const isHq = mode === 'HQ';
    // 视角语义：
    //   - DSP：dspId 由 useCurrentDsp 注入；warehouseId 来自 DeliveryLayout 顶部仓库选择器
    //   - wms：dspId 不带（看全部 DSP）；warehouseId 来自 WmsLayout 顶部仓库选择器
    //   - HQ ：dspId 不带（看全部 DSP）；warehouseId 不带（全美，QC 布局无仓库选择器）
    const numericDspId = !isViewOnly && dspId ? Number(dspId) : null;
    const {currentWarehouse} = useWarehouseContext();
    const numericWarehouseId = isHq
        ? null
        : currentWarehouse?.id ? Number(currentWarehouse.id) : null;

    const [activeTab, setActiveTab] = useState<CustomerComplaintTab>(CustomerComplaintTab.PENDING);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [detailVisible, setDetailVisible] = useState(false);
    const [currentComplaintId, setCurrentComplaintId] = useState<number | undefined>();

    // 放弃申诉确认弹窗的目标行 + 触发后实际调用的动作（区分 acknowledge / abandon）
    const [abandonTarget, setAbandonTarget] = useState<
        {record: CustomerComplaint; action: 'acknowledge' | 'abandon'} | null
    >(null);
    const [abandonLoading, setAbandonLoading] = useState(false);

    // 导出（仅 HQ）：异步生成后弹窗提示去下载中心领取，对齐 ReviewTaskPage 的冷却 + 弹窗范式
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    const {downloadLoading, setDownloadLoading, cooldownRemaining, getLastDownloadTime, setLastDownloadTime} =
        useDownloadCooldown(`customerComplaintDownloadLastTime_${getUserInfo()?.account?.id}`);
    const [generateDownloadReport] = useMutation<
        GenerateCustomerComplaintDownloadReportResponse,
        GenerateCustomerComplaintDownloadReportVariables
    >(GENERATE_CUSTOMER_COMPLAINT_DOWNLOAD_REPORT);

    const [filters, setFilters] = useState<CustomerComplaintFilters>(getDefaultCustomerComplaintFilters);
    // 已生效的筛选条件（点查询/重置时才同步），避免输入即触发查询
    const [appliedFilters, setAppliedFilters] = useState<CustomerComplaintFilters>(filters);

    const {data, loading, totalElements, executeQuery} = useCustomerComplaintList(mode);
    const [abandonAppeal] = useAbandonCustomerComplaintAppeal();
    const [acknowledgeComplaint] = useAcknowledgeCustomerComplaint();

    const appliedBaseFilter = useMemo(
        () => {
            // HQ 不依赖仓库，直接构造全美 filter；DSP/wms 未选仓库时为 null（不发请求）
            if (isHq) return toHqBaseFilterInput(appliedFilters);
            return numericWarehouseId
                ? toBaseFilterInput(appliedFilters, numericDspId, numericWarehouseId)
                : null;
        },
        [appliedFilters, isHq, numericDspId, numericWarehouseId],
    );

    const {getCount, refetchAll: refetchTabCounts} = useCustomerComplaintTabCounts(
        activeTab,
        appliedBaseFilter,
        totalElements,
        mode,
    );

    /**
     * 查询失败提示：仅「运单号条数超限」透出后端的具体上限，details 带 max，
     * 用户据此知道该删到多少条。其余错误走通用文案，后端 message 不宜直接展示
     */
    const notifyQueryError = useCallback(
        (e: unknown) => {
            if (extractGraphQLMessageKey(e) === TOO_MANY_TRACKING_NUMBERS_KEY) {
                // 只取词条用到的 max：传整个 details 会让 i18next 的 t() 重载解析到 returnDetails 分支
                const details = (e as any)?.graphQLErrors?.[0]?.extensions?.details as
                    | { max?: number }
                    | undefined;
                message.error(t(TOO_MANY_TRACKING_NUMBERS_KEY, {max: details?.max}));
                return;
            }
            message.error(t('customerComplaint.list.loadFailed'));
        },
        [t],
    );

    const runQuery = useCallback(
        (targetPage: number, targetSize: number, f: CustomerComplaintFilters, tab: CustomerComplaintTab) => {
            // 排序：待处理/申诉中按处理时效正序（PRD：距时效越近越靠上）；已完结按完结时间倒序
            const isClosedTab = tab === CustomerComplaintTab.CLOSED;
            const sortBy = isClosedTab ? 'FINISHED_AT' : 'APPEAL_DEADLINE_AT';
            const sortDirection = isClosedTab ? 'DESC' : 'ASC';
            if (isHq) {
                // HQ 全美：不带 warehouseId，无需选仓库即可查询
                executeQuery(
                    {
                        ...toHqBaseFilterInput(f),
                        ...TAB_TO_QUERY_PARAMS[tab],
                        sortBy,
                        sortDirection,
                    },
                    targetPage,
                    targetSize,
                ).catch(notifyQueryError);
                return;
            }
            // DSP / wms：warehouseId 双端必填，未选仓库时不发请求，由 UI 显示"请先选择仓库"
            if (!numericWarehouseId) return;
            executeQuery(
                {
                    ...toBaseFilterInput(f, numericDspId, numericWarehouseId),
                    ...TAB_TO_QUERY_PARAMS[tab],
                    sortBy,
                    sortDirection,
                },
                targetPage,
                targetSize,
            ).catch(notifyQueryError);
        },
        [executeQuery, isHq, notifyQueryError, numericDspId, numericWarehouseId],
    );

    /** 导出当前 Tab + 当前已生效筛选的全量（异步生成，去下载中心领取） */
    const handleExport = useCallback(async () => {
        const currentTime = Date.now();
        // 双重防抖：本地冷却时间 + loading 态，避免重复触发后端异步任务
        if (currentTime - getLastDownloadTime() < 5000 || downloadLoading) {
            message.warning(t('customerComplaint.export.generating'));
            return;
        }
        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime);
            const isClosedTab = activeTab === CustomerComplaintTab.CLOSED;
            // 用 appliedFilters（已点查询生效的）而非 filters，保证导出范围与当前列表一致
            const exportFilter = {
                ...toHqBaseFilterInput(appliedFilters),
                ...TAB_TO_QUERY_PARAMS[activeTab],
                sortBy: (isClosedTab ? 'FINISHED_AT' : 'APPEAL_DEADLINE_AT') as 'FINISHED_AT' | 'APPEAL_DEADLINE_AT',
                sortDirection: (isClosedTab ? 'DESC' : 'ASC') as 'DESC' | 'ASC',
            };
            // 后端按 language 出三语表头；取当前 i18n 短码（zh/en/es）
            const language = i18n.language.split('-')[0];
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = await generateDownloadReport({
                variables: {filter: exportFilter, language, timezone},
            });
            const result = response.data?.generateCustomerComplaintDownloadReportForHq;
            if (result?.success && result.fileName) {
                setGeneratedFileName(result.fileName);
                setDownloadModalVisible(true);
            } else if (result?.errorCode?.startsWith('EXCEED_MAX_LIMIT')) {
                const max = result.errorCode.split(':')[1] ?? '';
                message.error(t('customerComplaint.export.exceedMaxLimit', {max}));
            } else if (result?.errorCode === 'NO_DATA') {
                message.warning(t('customerComplaint.export.noData'));
            } else {
                message.error(t('customerComplaint.export.failed'));
            }
        } catch (e) {
            if (extractGraphQLMessageKey(e) === TOO_MANY_TRACKING_NUMBERS_KEY) {
                notifyQueryError(e);
            } else {
                message.error(t('customerComplaint.export.failed'));
            }
            setDownloadLoading(false);
        }
    }, [
        activeTab,
        appliedFilters,
        downloadLoading,
        generateDownloadReport,
        getLastDownloadTime,
        i18n.language,
        notifyQueryError,
        setDownloadLoading,
        setLastDownloadTime,
        t,
    ]);

    useEffect(() => {
        setPage(1);
        runQuery(1, pageSize, appliedFilters, activeTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, numericDspId, numericWarehouseId, isHq]);

    const handlePageChange = (nextPage: number, nextSize: number) => {
        if (nextSize !== pageSize) {
            setPage(1);
            setPageSize(nextSize);
            runQuery(1, nextSize, appliedFilters, activeTab);
        } else {
            setPage(nextPage);
            runQuery(nextPage, nextSize, appliedFilters, activeTab);
        }
    };

    const handleSearch = () => {
        setAppliedFilters(filters);
        setPage(1);
        runQuery(1, pageSize, filters, activeTab);
    };

    const handleReset = () => {
        const def = getDefaultCustomerComplaintFilters();
        setFilters(def);
        setAppliedFilters(def);
        setPage(1);
        runQuery(1, pageSize, def, activeTab);
    };

    const refresh = useCallback(() => {
        runQuery(page, pageSize, appliedFilters, activeTab);
        refetchTabCounts();
    }, [runQuery, page, pageSize, appliedFilters, activeTab, refetchTabCounts]);

    const handleOpenDetail = (record: CustomerComplaint) => {
        setCurrentComplaintId(record.id);
        setDetailVisible(true);
    };

    // 用户视角的"放弃申诉"按钮：仅打开确认弹窗，真正接口分发延迟到确认时
    //   PENDING_DSP_ACTION → acknowledgeCustomerComplaint（未申诉，主动认责）
    //   APPEALING          → abandonCustomerComplaintAppeal（已申诉，含级联 cancel review_task）
    const handleAcknowledge = (record: CustomerComplaint) => {
        setAbandonTarget({record, action: 'acknowledge'});
    };
    const handleAbandon = (record: CustomerComplaint) => {
        setAbandonTarget({record, action: 'abandon'});
    };

    const handleAbandonCancel = () => {
        if (abandonLoading) return;
        setAbandonTarget(null);
    };

    const handleAbandonConfirm = async () => {
        if (!abandonTarget) return;
        const {record, action} = abandonTarget;
        setAbandonLoading(true);
        try {
            if (action === 'acknowledge') {
                await acknowledgeComplaint({variables: {complaintId: record.id}});
                message.success(t('customerComplaint.drawer.acknowledgeSuccess') as string);
            } else {
                await abandonAppeal({variables: {complaintId: record.id}});
                message.success(t('customerComplaint.drawer.abandonSuccess') as string);
            }
            setAbandonTarget(null);
            refresh();
        } catch (e) {
            const failKey =
                action === 'acknowledge'
                    ? 'customerComplaint.drawer.acknowledgeFailed'
                    : 'customerComplaint.drawer.abandonFailed';
            message.error(t(failKey) as string);
        } finally {
            setAbandonLoading(false);
        }
    };

    if (!isHq && !numericWarehouseId) {
        // 仓库选择器还没 ready 或用户没选仓库：DSP / wms 双端 warehouseId 必填；HQ 全美不依赖仓库
        return (
            <div className="flex-1 flex items-center justify-center">
                <Empty description={t('customerComplaint.noWarehouse')} />
            </div>
        );
    }

    if (!isViewOnly && !dspId) {
        // DSP 视角进入但当前账号未绑定 DSP — 业务上不应出现，给空状态兜底
        return (
            <div className="flex-1 flex items-center justify-center">
                <Empty description={t('customerComplaint.noDsp')} />
            </div>
        );
    }

    // HQ 视角 Tab 文案不同（待dsp处理 / 申诉审核中 / 已完结），走独立 i18n key，不动 DSP/wms 现有文案
    const tabLabelPrefix = isHq ? 'customerComplaint.hqTabs' : 'customerComplaint.tabs';
    const tabItems = [
        {key: CustomerComplaintTab.PENDING, labelKey: `${tabLabelPrefix}.PENDING`},
        {key: CustomerComplaintTab.APPEALING, labelKey: `${tabLabelPrefix}.APPEALING`},
        {key: CustomerComplaintTab.CLOSED, labelKey: `${tabLabelPrefix}.CLOSED`},
    ].map(({key, labelKey}) => {
        const count = getCount(key);
        return {key, label: `${t(labelKey)} (${count === undefined ? '...' : count})`};
    });

    return (
        <div className="flex-1 flex flex-col">
            <CustomerComplaintFilter
                filters={filters}
                onChange={setFilters}
                onSearch={handleSearch}
                onReset={handleReset}
                dspId={numericDspId}
                warehouseId={numericWarehouseId}
                isViewOnly={isViewOnly}
                isHq={isHq}
                extraActions={
                    // 导出仅 HQ 开放（后端 mutation 亦只挂 QC 资源码）；与查询 / 重置同排，避免单独占一行
                    isHq ? (
                        <Button
                            type="primary"
                            onClick={() => void handleExport()}
                            loading={cooldownRemaining > 0}
                            disabled={downloadLoading || cooldownRemaining > 0}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">
                                {cooldownRemaining > 0
                                    ? `${t('customerComplaint.export.button')} ${cooldownRemaining}s`
                                    : t('customerComplaint.export.button')}
                            </span>
                        </Button>
                    ) : undefined
                }
            />

            <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key as CustomerComplaintTab)}
                items={tabItems}
            />

            <CustomerComplaintTable
                data={data}
                loading={loading}
                onOpenDetail={handleOpenDetail}
                // DSP 视角所有行同属当前 DSP，无需展示该列
                showDsp={isViewOnly}
                // 已完结 Tab 行内无可操作动作；仅查看视角同理，统一不传回调
                onAcknowledge={
                    isViewOnly || activeTab === CustomerComplaintTab.CLOSED
                        ? undefined
                        : handleAcknowledge
                }
                onAbandon={
                    isViewOnly || activeTab === CustomerComplaintTab.CLOSED
                        ? undefined
                        : handleAbandon
                }
            />

            <TablePaginationBar
                total={totalElements}
                current={page}
                pageSize={pageSize}
                onChange={handlePageChange}
                maxPages={MAX_PAGES}
            />

            <CustomerComplaintDetailDrawer
                open={detailVisible}
                complaintId={currentComplaintId}
                viewOnly={isViewOnly}
                onClose={() => setDetailVisible(false)}
                onChanged={refresh}
            />

            {isHq && (
                <ParcelsDownloadModal
                    visible={downloadModalVisible}
                    onClose={() => setDownloadModalVisible(false)}
                    fileName={generatedFileName}
                />
            )}

            <AbandonConfirmModal
                open={abandonTarget !== null}
                loading={abandonLoading}
                onCancel={handleAbandonCancel}
                onConfirm={handleAbandonConfirm}
            />
        </div>
    );
};

export default CustomerComplaintPanel;
